import { FieldDescriptor } from "./fieldTypes";

/**
 * The catalog **pack-entry** (`CatalogPackEntry`) properties panel's field
 * registry — the PLACEMENT, not the referenced pack `manifest.json` document
 * (that's `packFields.ts`; see docs/343 §2's "Placement vs. Document" split).
 *
 * Checked against a local, hand-verified key list for the same reason
 * `catalogFields.ts` documents: `@equipped/core`'s hand-written `CatalogPackEntry`
 * interface is missing `castable`/`tvMedia`, which the real `CatalogPackEntrySchema`
 * (Zod) does parse. Excludes `kind` (fixed `"pack"`) and `manifestUrl` (computed/
 * edited via the entry panel's own dedicated control, not a generic text field —
 * see `EntryPanel`).
 */
type CatalogPackEntryFieldKey =
  | "id"
  | "title"
  | "version"
  | "updated"
  | "icon"
  | "tags"
  | "access"
  | "languages"
  | "downloadable"
  | "placementId"
  | "description"
  | "visibility"
  | "platforms"
  | "castable"
  | "tvMedia"
  | "related"
  | "sameAs"
  | "group"
  | "level"
  | "terms";

export const catalogPackEntryFields = {
  id: { section: "Identity", label: "Entry id", kind: "text" },
  title: { section: "Identity", label: "Title", kind: "text" },
  version: { section: "Identity", label: "Version (semver, cheap browse-time hint)", kind: "text" },
  updated: { section: "Identity", label: "Updated (YYYY-MM-DD)", kind: "text" },
  icon: { section: "Identity", label: "Icon URL", kind: "text" },
  description: { section: "Identity", label: "Description", kind: "textarea" },
  placementId: {
    section: "Identity",
    label: "Placement id",
    kind: "text",
    help: "Globally unique per-appearance id — leave blank unless this pack is cross-listed and needs one.",
  },

  tags: { section: "Discovery", label: "Tags", kind: "stringArray" },
  access: {
    section: "Discovery",
    label: "Access",
    kind: "select",
    options: [
      { value: "free", label: "free" },
      { value: "paid", label: "paid" },
    ],
  },
  languages: { section: "Discovery", label: "Languages this pack offers", kind: "stringArray" },
  related: { section: "Discovery", label: "Related courses (see-also)", kind: "relatedRefs" },
  sameAs: { section: "Discovery", label: "Same-as key", kind: "text" },
  group: { section: "Discovery", label: "Group (section label)", kind: "text" },
  level: { section: "Discovery", label: "Level (course number)", kind: "number", integer: true },

  downloadable: { section: "Access & capabilities", label: "Downloadable for offline use", kind: "boolean3" },
  visibility: {
    section: "Access & capabilities",
    label: "Visibility",
    kind: "select",
    options: [
      { value: "public", label: "public" },
      { value: "private", label: "private" },
      { value: "hidden", label: "hidden" },
    ],
  },
  platforms: { section: "Access & capabilities", label: "Platform availability (opt-out)", kind: "recordBoolean" },
  castable: { section: "Access & capabilities", label: "Castable (AirPlay/Chromecast)", kind: "boolean3" },
  tvMedia: { section: "Access & capabilities", label: "Has TV media (skip-hint)", kind: "boolean3" },
  terms: { section: "Access & capabilities", label: "Terms of usage (this placement)", kind: "catalogTerms" },
} satisfies Record<CatalogPackEntryFieldKey, FieldDescriptor>;
